import React from 'react';
import { X, Shield, Eye, Lock, Database, Mail, Phone } from 'lucide-react';

interface PrivacyPolicyPageProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-lg">
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl border border-gray-600/50 shadow-2xl shadow-purple-600/30 animate-fade-in-up overflow-y-auto">
        {/* Gradient Border Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/30 via-purple-500/30 to-emerald-400/30 rounded-3xl blur-lg -z-10"></div>
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 md:p-8 border-b border-gray-700/50 sticky top-0 bg-gray-900/90 backdrop-blur-sm rounded-t-3xl">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-robotic mb-2">Privacy Policy</h2>
            <p className="text-gray-400 font-tech text-sm md:text-base">How we protect and handle your personal information</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-red-500/20 hover:border-red-500/30 border border-gray-700/50 transition-all duration-300 hover:scale-110 flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-400 hover:text-red-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 space-y-8">
          {/* Last Updated */}
          <div className="text-center p-4 bg-blue-500/10 rounded-xl border border-blue-400/20">
            <p className="text-blue-400 font-tech">Last updated: January 2025</p>
          </div>

          {/* Introduction */}
          <div>
            <div className="flex items-center mb-4">
              <Shield className="w-6 h-6 text-blue-400 mr-3" />
              <h3 className="text-2xl font-bold text-white font-robotic">Our Commitment to Your Privacy</h3>
            </div>
            <p className="text-gray-300 leading-relaxed font-tech">
              At DM.AI 4u, we are committed to protecting your privacy and ensuring the security of your personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
              AI-powered digital marketing services.
            </p>
          </div>

          {/* Information We Collect */}
          <div>
            <div className="flex items-center mb-4">
              <Database className="w-6 h-6 text-emerald-400 mr-3" />
              <h3 className="text-2xl font-bold text-white font-robotic">Information We Collect</h3>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                <h4 className="text-lg font-bold text-white mb-2 font-robotic">Personal Information</h4>
                <ul className="text-gray-300 space-y-1 font-tech">
                  <li>• Name, email address, and phone number</li>
                  <li>• Business information and company details</li>
                  <li>• Payment and billing information</li>
                  <li>• Communication preferences</li>
                </ul>
              </div>
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                <h4 className="text-lg font-bold text-white mb-2 font-robotic">Usage Information</h4>
                <ul className="text-gray-300 space-y-1 font-tech">
                  <li>• Website usage data and analytics</li>
                  <li>• Service interaction logs</li>
                  <li>• Device and browser information</li>
                  <li>• IP address and location data</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How We Use Information */}
          <div>
            <div className="flex items-center mb-4">
              <Eye className="w-6 h-6 text-purple-400 mr-3" />
              <h3 className="text-2xl font-bold text-white font-robotic">How We Use Your Information</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                <h4 className="text-lg font-bold text-white mb-2 font-robotic">Service Delivery</h4>
                <ul className="text-gray-300 space-y-1 font-tech text-sm">
                  <li>• Provide AI marketing services</li>
                  <li>• Process payments and billing</li>
                  <li>• Customer support and communication</li>
                  <li>• Service improvements and updates</li>
                </ul>
              </div>
              <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                <h4 className="text-lg font-bold text-white mb-2 font-robotic">Business Operations</h4>
                <ul className="text-gray-300 space-y-1 font-tech text-sm">
                  <li>• Analytics and performance monitoring</li>
                  <li>• Marketing and promotional communications</li>
                  <li>• Legal compliance and fraud prevention</li>
                  <li>• Research and development</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Data Protection */}
          <div>
            <div className="flex items-center mb-4">
              <Lock className="w-6 h-6 text-red-400 mr-3" />
              <h3 className="text-2xl font-bold text-white font-robotic">Data Protection & Security</h3>
            </div>
            <div className="p-6 bg-gradient-to-r from-red-500/10 to-purple-500/10 rounded-xl border border-red-400/20">
              <ul className="text-gray-300 space-y-2 font-tech">
                <li>• <strong className="text-white">Encryption:</strong> All data is encrypted in transit and at rest using industry-standard protocols</li>
                <li>• <strong className="text-white">Access Control:</strong> Strict access controls and authentication measures</li>
                <li>• <strong className="text-white">Regular Audits:</strong> Periodic security assessments and vulnerability testing</li>
                <li>• <strong className="text-white">GDPR Compliance:</strong> Full compliance with UK and EU data protection regulations</li>
                <li>• <strong className="text-white">Data Minimization:</strong> We only collect and retain necessary information</li>
              </ul>
            </div>
          </div>

          {/* Your Rights */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4 font-robotic">Your Rights</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-400/20">
                <h4 className="text-lg font-bold text-emerald-400 mb-2 font-robotic">Data Rights</h4>
                <ul className="text-gray-300 space-y-1 font-tech text-sm">
                  <li>• Access your personal data</li>
                  <li>• Correct inaccurate information</li>
                  <li>• Delete your data (right to be forgotten)</li>
                  <li>• Data portability</li>
                </ul>
              </div>
              <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-400/20">
                <h4 className="text-lg font-bold text-blue-400 mb-2 font-robotic">Communication Rights</h4>
                <ul className="text-gray-300 space-y-1 font-tech text-sm">
                  <li>• Opt-out of marketing communications</li>
                  <li>• Restrict processing of your data</li>
                  <li>• Object to automated decision-making</li>
                  <li>• Lodge complaints with authorities</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Cookies */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4 font-robotic">Cookies & Tracking</h3>
            <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
              <p className="text-gray-300 leading-relaxed font-tech mb-3">
                We use cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content. 
                You can control cookie preferences through your browser settings.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-tech">Essential Cookies</span>
                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-tech">Analytics Cookies</span>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-tech">Marketing Cookies</span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="p-6 bg-gradient-to-r from-purple-600/10 via-blue-500/10 to-emerald-400/10 rounded-xl border border-gray-700/50">
            <h3 className="text-2xl font-bold text-white mb-4 font-robotic">Contact Us About Privacy</h3>
            <p className="text-gray-300 mb-4 font-tech">
              If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us:
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center text-gray-300">
                <Mail className="w-5 h-5 mr-2 text-blue-400" />
                <span className="font-tech">dom@dm4u.ai</span>
              </div>
              <div className="flex items-center text-gray-300">
                <Phone className="w-5 h-5 mr-2 text-emerald-400" />
                <a href="tel:+447508300353" className="font-tech hover:text-blue-400 transition-colors duration-300">07508 300353</a>
              </div>
            </div>
          </div>

          {/* Updates */}
          <div className="text-center p-4 bg-yellow-500/10 rounded-xl border border-yellow-400/20">
            <p className="text-yellow-400 font-tech">
              We may update this Privacy Policy periodically. We will notify you of any material changes via email or website notice.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;